import { registerDestructor, isDestroying, isDestroyed, associateDestroyableChild, destroy } from '@ember/destroyable'
import { setOwner, getOwner } from '@ember/owner'
import environment from 'customer-flows/config/environment'
import { StoreWrapper, createForwardingProxy } from './store-proxy'

type StoreEntry<T extends Store> = {
  proxy: T
  wrapper: StoreWrapper<T>
}

const stores = import.meta.glob('../../stores/**/*.ts', { eager: true, import: 'default' })
const storeEntries = new Map<StoreName, StoreEntry<any>>()

/**
 * Lookup a store instance.
 *
 * If you need to access the whole store, use this function. It will return
 * the correct instance.
 */
export function lookupStore<T extends keyof StoreRegistry>(owner: any, storeName: T): StoreType<T> {
  if (isDestroying(owner) || isDestroyed(owner)) {
    let message = `Cannot look up store "${storeName}" because the owner is being destroyed or has been destroyed.`

    if (environment.isTesting) {
      message += ' This is likely due to an async operation resolving after the test has finished.'
    }

    throw new Error(message)
  }

  let entry = storeEntries.get(storeName)

  if (!entry) {
    const storeConstructor = stores[`../../stores/${storeName}.ts`] as StoreModule<T>

    if (!storeConstructor) {
      throw new Error(`Store "${storeName}" could not be loaded. Ensure stores/${storeName}.ts exports a default class.`)
    }

    const real = new storeConstructor()
    setOwner(real, owner)
    associateDestroyableChild(owner, real)

    const wrapper = new StoreWrapper(real)
    const proxy = createForwardingProxy<StoreType<T>>(wrapper)

    setOwner(proxy, owner)
    owner.register(`store:${storeName}`, proxy) // can only use `owner.lookup` to find stores that are already registered rn.
    associateDestroyableChild(owner, proxy)
    registerDestructor(proxy, () => _destroyStore(storeName))

    entry = { proxy, wrapper }
    storeEntries.set(storeName, entry)
  }

  return entry.proxy
}

/**
 * Resolves a property from a store module.
 * Takes a fully qualified field key (eg. `tickets:selectedTickets`)
 * or a store name and property key (eg. `tickets`, `selectedTickets`).
 *
 * This is a polymorphic utility that can be used in tests to mock store values
 * and in production to access store values.
 *
 * Generally used under the hood by the `@context` decorator, quote field resolvers,
 * and the stubContext test helper.
 *
 * If not using the `@context` decorator, you can use this function to access
 * store values. However, it's recommended to use the `@context` decorator
 * instead for better type safety and ergonomics.
 */
export function resolveStoreProp<T extends keyof StoreRegistry, K extends StoreFieldKey<T>>(ctx: any, storeName: T, propertyKey: K): StoreFieldValue<T, K> {
  const owner = getOwner(ctx)
  if (!owner) {
    throw new Error('Unable to find the owner. Ensure you call this function with the correct `this` context.')
  }

  const store = lookupStore(owner, storeName) as StoreType<T>

  if (!store) {
    throw new Error(`could not resolve store "${storeName}".`)
  }

  if (!(propertyKey in store)) {
    throw new Error(`"${propertyKey as string}" could not be resolved in store "${storeName}".`)
  }

  // If it's a function, bind it to the store instance.
  const value = store[propertyKey]
  if (typeof value === 'function') {
    return value.bind(store)
  }

  return store[propertyKey]
}

/**
 * Resets all stores to their initial state, skipping the specified stores.
 *
 * This is useful for tests that need to reset the state between tests.
 * (Called automatically by the test framework after each test)
 *
 * It's also useful for flows that need to reset the state when the user
 * navigates away from the flow.
 * (Called by the 'flow' route when redirecting)
 *
 * @param skip - An array of store names to skip resetting
 */
export function resetAllStores(ctx: any, { skip = [] as StoreName[] } = {}) {
  const owner = getOwner(ctx)
  if (!owner) return

  for (const [storeName, entry] of storeEntries) {
    if (skip.includes(storeName)) continue

    const storeConstructor = entry.wrapper.current.constructor
    const next = new storeConstructor()

    setOwner(next, owner)
    associateDestroyableChild(owner, next)

    destroy(entry.wrapper.current as any)
    entry.wrapper.current = next
  }
}

/**
 * Resets a single store to its initial state.
 */
export function resetStore(ctx: any, storeName: StoreName) {
  const owner = getOwner(ctx)
  if (!owner) return

  const entry = storeEntries.get(storeName)
  if (!entry) return

  const storeConstructor = entry.wrapper.current.constructor
  const next = new storeConstructor()

  setOwner(next, owner)
  associateDestroyableChild(owner, next)

  destroy(entry.wrapper.current as any)
  entry.wrapper.current = next
}

/**
 * WARNING: This function should not be used outside of Ember's destroyable system.
 * ie. don't import and use it anywhere else. It will break any component that uses the store.
 */
function _destroyStore(storeName: StoreName) {
  const entry = storeEntries.get(storeName)
  if (!entry) return

  destroy(entry.wrapper.current as any)
  storeEntries.delete(storeName)
}
